import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatCurrency, formatApiError, API } from "@/lib/api";
import { Wrench, Search, FileSpreadsheet, FileText, Plus, Mail } from "lucide-react";
import { toast } from "sonner";
import { formatPhoneDisplay } from "@/lib/format";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

const STATUS_STYLES = {
  Overdue: "bg-red-100 text-red-800 border-red-300",
  "Due Soon": "bg-orange-100 text-orange-800 border-orange-300",
  Current: "bg-emerald-100 text-emerald-800 border-emerald-300",
  Unscheduled: "bg-zinc-100 text-zinc-700 border-zinc-300",
};

export default function Maintenance() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [sortBy, setSortBy] = useState("next_maintenance_date");
  const [logModalFor, setLogModalFor] = useState(null);
  // "Reports" drawer target — clicking opens a small modal listing all
  // maintenance visits on that deal and links each to its own Annual
  // Maintenance Report editor.
  const [reportsFor, setReportsFor] = useState(null);
  const [renewalFor, setRenewalFor] = useState(null); // {row, preview}
  const [renewalLoadingId, setRenewalLoadingId] = useState(null);

  const openRenewal = async (r) => {
    setRenewalLoadingId(r.id);
    try {
      const res = await api.get(`/deals/${r.id}/maintenance-renewal/preview`);
      setRenewalFor({ row: r, preview: res.data });
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    } finally {
      setRenewalLoadingId(null);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/maintenance");
      setRows(r.data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let out = rows;
    if (statusFilter !== "All") out = out.filter((r) => r.status === statusFilter);
    if (q) {
      out = out.filter((r) =>
        (r.contact_name || "").toLowerCase().includes(q) ||
        (r.title || "").toLowerCase().includes(q) ||
        (r.property_address || "").toLowerCase().includes(q) ||
        (r.contact_phone || "").toLowerCase().includes(q)
      );
    }
    out = [...out].sort((a, b) => {
      const va = a[sortBy] || "";
      const vb = b[sortBy] || "";
      if (sortBy === "maintenance_rate") return Number(vb) - Number(va);
      // empty dates sort last
      if (!va) return 1;
      if (!vb) return -1;
      return String(va).localeCompare(String(vb));
    });
    return out;
  }, [rows, search, statusFilter, sortBy]);

  const totals = useMemo(() => {
    const annualRev = filtered.reduce((s, r) => s + Number(r.maintenance_rate || 0), 0);
    const overdue = filtered.filter((r) => r.status === "Overdue").length;
    const due30 = filtered.filter((r) => r.status === "Due Soon" || r.status === "Overdue").length;
    const renewing60 = filtered.filter((r) => r.renewal_days_out != null && r.renewal_days_out <= 60).length;
    return { annualRev, overdue, due30, renewing60, count: filtered.length };
  }, [filtered]);

  const [ytdIncome, setYtdIncome] = useState(0);
  const [revByMonth, setRevByMonth] = useState([]);
  useEffect(() => {
    api.get("/maintenance/revenue-by-month").then((r) => setRevByMonth(r.data || [])).catch(() => setRevByMonth([]));
  }, [rows]);
  useEffect(() => {
    api.get("/dashboard/revenue-by-type?window=ytd").then((r) => {
      const m = (r.data?.rows || []).find((x) => x.project_type === "Maintenance");
      setYtdIncome(m?.received || 0);
    }).catch(() => setYtdIncome(0));
  }, [rows]);

  const downloadList = async (fmt) => {
    const token = localStorage.getItem("crm_token");
    try {
      const r = await fetch(`${API}/maintenance/export.${fmt}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error(`Export failed (${r.status})`);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `sealtech-maintenance.${fmt}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(e.message || "Download failed");
    }
  };

  return (
    <div className="p-6 sm:p-8 animate-in fade-in duration-500" data-testid="maintenance-page">
      <div className="flex items-start justify-between mb-8 pb-6 border-b border-zinc-200 gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Wrench className="w-4 h-4 text-blue-700" />
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-blue-700">Recurring</div>
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight">Maintenance Plans</h1>
          <div className="mt-2 text-xs uppercase tracking-wider text-zinc-500">Track annual visits, due dates, and recurring revenue</div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => downloadList("xlsx")}
            className="inline-flex items-center gap-2 border border-zinc-300 text-zinc-700 px-4 h-10 text-xs font-bold uppercase tracking-wider hover:border-zinc-950 rounded-sm transition-colors"
            data-testid="export-maintenance-xlsx"
          >
            <FileSpreadsheet className="w-4 h-4" /> Excel
          </button>
          <button
            onClick={() => downloadList("pdf")}
            className="inline-flex items-center gap-2 bg-zinc-950 text-white px-4 h-10 text-xs font-bold uppercase tracking-wider hover:bg-zinc-800 rounded-sm transition-colors"
            data-testid="export-maintenance-pdf"
          >
            <FileText className="w-4 h-4" /> PDF
          </button>
        </div>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-4 mb-6">
        <KpiCard label="Customers on Plan" value={totals.count} testId="kpi-maint-count" />
        <KpiCard label="Annual Recurring Revenue" value={formatCurrency(totals.annualRev)} hint="Contracted rate" testId="kpi-maint-arr" accent="text-emerald-700" />
        <KpiCard label="Visits Income (YTD)" value={formatCurrency(ytdIncome)} hint="Actual income from logged visits" testId="kpi-maint-ytd-income" accent="text-emerald-700" />
        <KpiCard label="Due Within 30 Days" value={totals.due30} testId="kpi-maint-due30" accent="text-orange-700" />
        <KpiCard label="Renewals ≤60 Days" value={totals.renewing60} hint="Agreement anniversaries" testId="kpi-maint-renewals" accent="text-amber-700" />
        <KpiCard label="Overdue" value={totals.overdue} testId="kpi-maint-overdue" accent="text-red-700" />
      </div>

      {/* Recurring income by month */}
      <div className="bg-white border border-zinc-200 rounded-sm p-4 mb-6" data-testid="maint-revenue-chart">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">Maintenance Income by Month</div>
          <div className="text-[10px] uppercase tracking-wider text-zinc-400">Last 12 months · from logged visits</div>
        </div>
        <div className="h-44">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={revByMonth} margin={{ top: 4, right: 4, bottom: 0, left: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#71717A" }} axisLine={false} tickLine={false} />
              <YAxis tickFormatter={(v) => (v >= 1000 ? `$${Math.round(v / 1000)}k` : `$${v}`)} tick={{ fontSize: 10, fill: "#71717A" }} axisLine={false} tickLine={false} width={44} />
              <Tooltip formatter={(v) => [formatCurrency(v), "Income"]} labelStyle={{ fontWeight: 700 }} cursor={{ fill: "rgba(22,163,74,0.08)" }} />
              <Bar dataKey="amount" fill="#16A34A" radius={[2, 2, 0, 0]} maxBarSize={38} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-zinc-200 rounded-sm p-4 mb-6">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex-1 min-w-[240px] relative">
            <Search className="w-4 h-4 text-zinc-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Search by customer, property, phone..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full h-9 pl-9 pr-3 border border-zinc-300 rounded-sm text-sm"
              data-testid="maintenance-search"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="h-9 px-3 border border-zinc-300 rounded-sm text-sm bg-white"
            data-testid="maintenance-status-filter"
          >
            <option value="All">All Statuses</option>
            <option value="Overdue">Overdue</option>
            <option value="Due Soon">Due Soon (≤30 days)</option>
            <option value="Current">Current (done — next visit scheduled)</option>
            <option value="Unscheduled">Unscheduled</option>
          </select>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="h-9 px-3 border border-zinc-300 rounded-sm text-sm bg-white"
            data-testid="maintenance-sort"
          >
            <option value="next_maintenance_date">Sort: Next Due Date</option>
            <option value="contact_name">Sort: Customer Name</option>
            <option value="property_address">Sort: Property Address</option>
            <option value="maintenance_rate">Sort: Annual Rate (High→Low)</option>
            <option value="last_maintenance_date">Sort: Last Visit Date</option>
            <option value="renewal_date">Sort: Renewal Date</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-zinc-200 rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="maintenance-table">
            <thead>
              <tr className="border-b-2 border-zinc-950 text-left text-[10px] uppercase tracking-wider bg-zinc-50">
                <th className="py-3 px-4">Customer</th>
                <th className="py-3 px-4">Property</th>
                <th className="py-3 px-4 text-right">Annual Rate</th>
                <th className="py-3 px-4">Last Visit</th>
                <th className="py-3 px-4">Next Due</th>
                <th className="py-3 px-4">Renewal</th>
                <th className="py-3 px-4">Status</th>
                <th className="py-3 px-4 text-right">Visits</th>
                <th className="py-3 px-4"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} className="py-8 text-center text-xs uppercase tracking-wider text-zinc-500">Loading...</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={9} className="py-8 text-center text-sm text-zinc-500">No maintenance plans match. Toggle Maintenance Plan on a Project to see it here.</td></tr>
              ) : (
                filtered.map((r) => (
                  <tr key={r.id} className="border-b border-zinc-100 hover:bg-blue-50/40" data-testid={`maint-row-${r.id}`}>
                    <td className="py-3 px-4">
                      <div className="font-bold text-zinc-950">{r.contact_name || "—"}</div>
                      <div className="text-[11px] text-zinc-500">{formatPhoneDisplay(r.contact_phone)}</div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="text-zinc-700">{r.property_name || r.title || "—"}</div>
                      <div className="text-[11px] text-zinc-500">{r.property_address || ""}</div>
                    </td>
                    <td className="py-3 px-4 text-right font-mono">
                      {formatCurrency(r.maintenance_rate)}
                      {r.maintenance_plan_type && (
                        <div className="text-[10px] text-zinc-500 uppercase tracking-wider font-sans">
                          {r.maintenance_plan_type === "biannual" ? "Semi-Annual" : "Annual"}
                        </div>
                      )}
                      {r.rate_fixed ? (
                        <div className="text-[10px] text-blue-700 font-bold font-sans" data-testid={`rate-fixed-${r.id}`}>
                          Fixed thru {r.maintenance_rate_locked_until}
                        </div>
                      ) : Number(r.maintenance_escalator_pct) > 0 ? (
                        <div className="text-[10px] text-emerald-700 font-sans" data-testid={`rate-escalator-${r.id}`}>
                          +{r.maintenance_escalator_pct}%/yr → {formatCurrency(r.next_renewal_rate)}
                        </div>
                      ) : null}
                    </td>
                    <td className="py-3 px-4 font-mono text-zinc-700">{r.last_maintenance_date || "—"}</td>
                    <td className="py-3 px-4 font-mono font-bold">{r.next_maintenance_date || "—"}</td>
                    <td className="py-3 px-4" data-testid={`maint-renewal-${r.id}`}>
                      {r.renewal_date ? (
                        <>
                          <div className="font-mono text-zinc-700">{r.renewal_date}</div>
                          <div className={`text-[10px] uppercase tracking-wider font-bold ${r.renewal_days_out <= 60 ? "text-amber-700" : "text-zinc-500"}`}>
                            in {r.renewal_days_out} days
                          </div>
                        </>
                      ) : (
                        <span className="text-zinc-400">—</span>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <span className={`inline-flex items-center px-2 h-6 text-[10px] font-bold uppercase tracking-wider border rounded-sm ${STATUS_STYLES[r.status]}`}>
                        {r.status}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-right font-sans font-semibold tabular-nums text-zinc-600">{r.visit_count}</td>
                    <td className="py-3 px-4 text-right">
                      <div className="inline-flex items-center gap-1 flex-wrap justify-end">
                        <button
                          onClick={() => setLogModalFor(r)}
                          className="inline-flex items-center gap-1 px-2 h-7 text-[10px] font-bold uppercase tracking-wider bg-blue-700 text-white hover:bg-blue-800 rounded-sm"
                          data-testid={`log-visit-${r.id}`}
                        >
                          <Plus className="w-3 h-3" /> Log Visit
                        </button>
                        {(r.visit_count > 0) && (
                          <button
                            onClick={() => setReportsFor(r)}
                            className="inline-flex items-center gap-1 px-2 h-7 text-[10px] font-bold uppercase tracking-wider border border-zinc-950 text-zinc-950 hover:bg-zinc-950 hover:text-white rounded-sm"
                            data-testid={`open-reports-${r.id}`}
                            title="Open a past visit's Annual Maintenance Report editor"
                          >
                            <FileText className="w-3 h-3" /> Reports ({r.visit_count})
                          </button>
                        )}
                        {r.renewal_date && (
                          <button
                            onClick={() => openRenewal(r)}
                            disabled={renewalLoadingId === r.id}
                            className="inline-flex items-center gap-1 px-2 h-7 text-[10px] font-bold uppercase tracking-wider bg-amber-600 text-white hover:bg-amber-700 rounded-sm disabled:opacity-50"
                            data-testid={`send-renewal-${r.id}`}
                            title="Preview & send the renewal letter (agreement + invoice)"
                          >
                            <Mail className="w-3 h-3" /> {renewalLoadingId === r.id ? "Loading..." : "Send Renewal"}
                          </button>
                        )}
                        <Link
                          to={`/projects/${r.id}`}
                          className="px-2 h-7 inline-flex items-center text-[10px] font-bold uppercase tracking-wider border border-zinc-300 hover:border-zinc-950 rounded-sm"
                          data-testid={`open-project-${r.id}`}
                        >
                          Open
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {logModalFor && (
        <LogVisitModal
          row={logModalFor}
          onClose={() => setLogModalFor(null)}
          onSaved={() => { setLogModalFor(null); load(); }}
        />
      )}
      {reportsFor && (
        <ReportsListModal
          row={reportsFor}
          onClose={() => setReportsFor(null)}
        />
      )}
      {renewalFor && (
        <RenewalPreviewModal
          row={renewalFor.row}
          preview={renewalFor.preview}
          onClose={() => setRenewalFor(null)}
          onSent={() => setRenewalFor(null)}
        />
      )}
    </div>
  );
}

/**
 * RenewalPreviewModal — review everything before the renewal letter goes out:
 * recipient (editable), plan, amount, renewal date, optional note, and a
 * one-click view of the agreement PDF. Nothing sends until "Send Renewal".
 */
function RenewalPreviewModal({ row, preview, onClose, onSent }) {
  const [toEmail, setToEmail] = useState(preview.to_email || "");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  const viewAgreement = async () => {
    try {
      const r = await api.get(`/deals/${row.id}/maintenance-agreement.pdf`, { responseType: "blob" });
      window.open(URL.createObjectURL(new Blob([r.data], { type: "application/pdf" })), "_blank");
    } catch { toast.error("Could not load the agreement PDF"); }
  };

  const send = async () => {
    if (!toEmail.trim()) { toast.error("Recipient email is required"); return; }
    setSending(true);
    try {
      const res = await api.post(`/deals/${row.id}/maintenance-renewal/email`, { to_email: toEmail.trim(), message });
      toast.success(res.data?.message || "Renewal letter sent");
      onSent();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-zinc-950/60 flex items-center justify-center p-4" onClick={() => !sending && onClose()} data-testid="renewal-preview-modal">
      <div className="bg-white w-full max-w-lg rounded-sm shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-zinc-200">
          <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">Renewal Letter — Preview Before Sending</div>
          <div className="font-heading text-xl font-black tracking-tight mt-1">{row.contact_name || row.title}</div>
          <div className="text-xs text-zinc-500 mt-0.5">{row.property_address}</div>
        </div>
        <div className="p-5 space-y-3">
          <div className="grid grid-cols-3 gap-2 text-center">
            <div className="border border-zinc-200 rounded-sm p-2">
              <div className="text-[9px] font-bold uppercase tracking-wider text-zinc-500">Renews</div>
              <div className="text-sm font-bold mt-0.5" data-testid="renewal-preview-date">{preview.renewal_date_display}</div>
            </div>
            <div className="border border-zinc-200 rounded-sm p-2">
              <div className="text-[9px] font-bold uppercase tracking-wider text-zinc-500">Plan</div>
              <div className="text-sm font-bold mt-0.5">{(preview.plan_label || "").split(" (")[0]}</div>
            </div>
            <div className="border border-zinc-200 rounded-sm p-2">
              <div className="text-[9px] font-bold uppercase tracking-wider text-zinc-500">Amount</div>
              <div className="text-sm font-bold mt-0.5 text-emerald-700" data-testid="renewal-preview-amount">{formatCurrency(preview.amount)}</div>
            </div>
          </div>
          {preview.escalated ? (
            <div className="text-[11px] text-emerald-700 font-semibold" data-testid="renewal-escalator-note">
              Includes the {preview.escalator_pct}% annual increase.
            </div>
          ) : preview.rate_locked_until ? (
            <div className="text-[11px] text-blue-700 font-semibold" data-testid="renewal-fixed-note">
              Rate is fixed through {preview.rate_locked_until} — no increase applied.
            </div>
          ) : null}
          <div className="text-[11px] text-zinc-500">
            Attachments: updated Maintenance Agreement PDF + renewal invoice
            {preview.existing_invoice_number ? ` (reuses ${preview.existing_invoice_number})` : " (drafted on send, due on the renewal date)"}.
            {" "}
            <button onClick={viewAgreement} className="text-blue-700 font-bold hover:underline" data-testid="renewal-preview-agreement">View agreement PDF</button>
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">To</label>
            <input type="email" value={toEmail} onChange={(e) => setToEmail(e.target.value)} placeholder="customer@email.com" data-testid="renewal-preview-to" className="mt-1 w-full h-9 px-2 border border-zinc-300 rounded-sm text-sm" />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">Personal note (optional)</label>
            <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={3} placeholder="e.g. Great seeing your team in March — looking forward to another year on the roof." data-testid="renewal-preview-message" className="mt-1 w-full px-2 py-1.5 border border-zinc-300 rounded-sm text-sm" />
          </div>
        </div>
        <div className="px-5 py-4 border-t border-zinc-200 flex justify-end gap-2">
          <button onClick={onClose} disabled={sending} className="px-4 h-9 text-xs font-bold uppercase tracking-wider border border-zinc-300 text-zinc-700 hover:border-zinc-950 rounded-sm" data-testid="renewal-preview-cancel">Cancel</button>
          <button onClick={send} disabled={sending} className="px-4 h-9 text-xs font-bold uppercase tracking-wider bg-amber-600 text-white hover:bg-amber-700 rounded-sm disabled:opacity-50" data-testid="renewal-preview-send">
            {sending ? "Sending..." : "Send Renewal"}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * ReportsListModal — small drawer listing every maintenance visit on a
 * deal so the user can jump into any historical visit's Annual
 * Maintenance Report editor. Fetches the deal fresh so the visit list
 * reflects any recent PATCH updates.
 */
function ReportsListModal({ row, onClose }) {
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/deals/${row.id}`);
        // Sort descending by visit_date so the most recent is on top
        const list = [...(r.data.maintenance_visits || [])].sort((a, b) => (b.visit_date || "").localeCompare(a.visit_date || ""));
        setVisits(list);
      } catch (e) {
        toast.error(formatApiError(e?.response?.data?.detail) || e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [row.id]);

  return (
    <div className="fixed inset-0 z-50 bg-zinc-950/60 flex items-center justify-center p-4" onClick={onClose} data-testid="reports-list-modal">
      <div className="bg-white w-full max-w-2xl rounded-sm shadow-xl max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-zinc-200">
          <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">Annual Maintenance Reports</div>
          <div className="font-heading text-xl font-black tracking-tight mt-1">{row.contact_name || row.title}</div>
          <div className="text-xs text-zinc-500 mt-0.5">{row.property_address}</div>
        </div>
        <div className="p-5">
          {loading ? (
            <div className="text-center text-sm text-zinc-500 py-6">Loading…</div>
          ) : visits.length === 0 ? (
            <div className="text-center text-sm text-zinc-500 py-6">No visits yet — log a visit to start a report.</div>
          ) : (
            <div className="space-y-2">
              {visits.map((v) => (
                <Link
                  key={v.id}
                  to={`/maintenance/report/${row.id}/${v.id}`}
                  onClick={onClose}
                  data-testid={`reports-list-item-${v.id}`}
                  className="block border border-zinc-200 rounded-sm p-3 hover:border-zinc-950 hover:bg-blue-50/40 transition-colors"
                >
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div>
                      <div className="font-bold text-sm">{v.visit_date}</div>
                      <div className="text-[10px] text-zinc-500 uppercase tracking-wider">
                        {v.service_life_estimate ? `Service Life · ${v.service_life_estimate}` : "No service-life set yet"}
                      </div>
                    </div>
                    <div className="text-[10px] text-zinc-500 uppercase tracking-wider">
                      {v.summary_text ? "Summary drafted" : "No summary yet"}
                    </div>
                    <FileText className="w-3.5 h-3.5 text-zinc-400" />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function LogVisitModal({ row, onClose, onSaved }) {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [amount, setAmount] = useState(row.maintenance_rate || 0);
  const [subs, setSubs] = useState([]);
  const [subId, setSubId] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/vendors?kind=Subcontractor").then((r) => setSubs(r.data)).catch(() => setSubs([]));
  }, []);

  const submit = async () => {
    if (!date) { toast.error("Visit date is required"); return; }
    setSaving(true);
    try {
      const payload = { visit_date: date, amount: Number(amount || 0), notes };
      if (subId) payload.subcontractor_id = subId;
      const visitRes = await api.post(`/deals/${row.id}/maintenance-visits`, payload);
      toast.success("Visit logged — next due date advanced");
      // Find the newly-added visit (highest id from response)
      const visits = visitRes.data?.maintenance_visits || [];
      const newest = [...visits].sort((a, b) => (b.visit_date || "").localeCompare(a.visit_date || ""))[0];
      // Offer to jump straight into the Annual Maintenance Report editor.
      // This is the primary path — the rep captured photos in the field
      // during the visit, now they want to add descriptions and generate
      // the client PDF.
      if (newest && window.confirm(`Open the Annual Maintenance Report editor for this visit? You'll be able to add photo descriptions, service-life estimate, and summary before generating the PDF.`)) {
        window.location.href = `/maintenance/report/${row.id}/${newest.id}`;
        return;
      }
      // Otherwise fall through to the older "auto-draft invoice" prompt
      if (newest && Number(amount || 0) > 0 && window.confirm(`Create a draft invoice for $${Number(amount).toLocaleString()}? This is enabled because most maintenance customers get billed per visit.`)) {
        try {
          const inv = await api.post("/invoices/from-maintenance-visit", { deal_id: row.id, visit_id: newest.id });
          toast.success(`Draft invoice ${inv.data.invoice_number} created — opening invoices`);
          setTimeout(() => { window.location.href = "/invoices"; }, 600);
          return;
        } catch (e) {
          toast.error("Visit logged, but invoice could not be auto-created");
        }
      }
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-zinc-950/60 flex items-center justify-center p-4" onClick={onClose} data-testid="log-visit-modal">
      <div className="bg-white w-full max-w-lg rounded-sm shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-zinc-200">
          <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">Log Maintenance Visit</div>
          <div className="font-heading text-xl font-black tracking-tight mt-1">{row.contact_name || row.title}</div>
          <div className="text-xs text-zinc-500 mt-0.5">{row.property_address}</div>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">Visit Date</label>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1 w-full h-9 px-2 border border-zinc-300 rounded-sm text-sm" data-testid="modal-visit-date" />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">Amount ($)</label>
            <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} className="mt-1 w-full h-9 px-2 border border-zinc-300 rounded-sm text-sm font-mono" data-testid="modal-visit-amount" />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">Subcontractor</label>
            <select value={subId} onChange={(e) => setSubId(e.target.value)} className="mt-1 w-full h-9 px-2 border border-zinc-300 rounded-sm text-sm bg-white" data-testid="modal-visit-sub">
              <option value="">— None / In-house —</option>
              {subs.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">Notes</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} className="mt-1 w-full px-2 py-1.5 border border-zinc-300 rounded-sm text-sm" placeholder="What was done? Any findings?" data-testid="modal-visit-notes" />
          </div>
        </div>
        <div className="px-5 py-4 border-t border-zinc-200 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 h-9 text-xs font-bold uppercase tracking-wider border border-zinc-300 text-zinc-700 hover:border-zinc-950 rounded-sm" data-testid="modal-cancel">Cancel</button>
          <button disabled={saving} onClick={submit} className="px-4 h-9 text-xs font-bold uppercase tracking-wider bg-blue-700 text-white hover:bg-blue-800 rounded-sm disabled:opacity-50" data-testid="modal-save-visit">
            {saving ? "Saving..." : "Log Visit"}
          </button>
        </div>
      </div>
    </div>
  );
}

const KpiCard = ({ label, value, hint, testId, accent }) => (
  <div className="bg-white border border-zinc-200 p-6 rounded-sm" data-testid={testId}>
    <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500 mb-3">{label}</div>
    <div className={`font-heading text-3xl font-black tracking-tighter ${accent || "text-zinc-950"}`}>{value}</div>
    {hint && <div className="text-xs text-zinc-500 mt-2">{hint}</div>}
  </div>
);
